import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetPropertyQuery,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavouritePropertyQuery,
} from '../../features/api/apiSlice';
import { selectIsAuthenticated, selectCurrentUser } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import SEO from '../../components/seo';
import { useToast } from '../../components/ui/Toast';
import {
  getTransactionBadgeText,
  getTransactionLabel,
  TRANSACTION_BADGE_STYLE,
  isLuxuryListing,
  isTakenListing,
} from '../../lib/propertyLabels';
import { useCurrency } from '../../features/currency/CurrencyContext';
import { trackEvent } from '../../lib/analytics';
import { listingWhatsapp, whatsappUrl } from '../../lib/whatsapp';
import {
  listingKeywords,
  listingPageDescription,
  listingPageTitle,
  listingSchema,
} from '../../lib/seo';

const findParameter = (parameters, name) => {
  const param = parameters?.find((p) => p.name?.toLowerCase() === name.toLowerCase());
  return param ? param.value : null;
};

const PropertyDetail = () => {
  const navigate = useNavigate();
  const { slug_id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authUser = useSelector(selectCurrentUser);
  const addToast = useToast();
  const { formatPrice, currency } = useCurrency();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: authUser?.name || '',
    email: authUser?.email || '',
    phone: authUser?.mobile || '',
    message: '',
  });

  const {
    data: property,
    isLoading,
    error,
    refetch: refetchProperty,
  } = useGetPropertyQuery(slug_id, { refetchOnMountOrArgChange: true });
  const previousAuthState = useRef(isAuthenticated);
  useEffect(() => {
    if (previousAuthState.current !== isAuthenticated) {
      previousAuthState.current = isAuthenticated;
      refetchProperty();
    }
  }, [isAuthenticated, refetchProperty]);
  useEffect(() => {
    if (property?.id) {
      trackEvent(
        'property_view',
        { property_id: property.id, slug: slug_id, title: property.title },
        'click'
      );
    }
  }, [property?.id, slug_id]);
  const { data: favourites = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });
  const [addFavorite, { isLoading: addingFav }] = useAddFavoriteMutation();
  const [deleteFavorite, { isLoading: removingFav }] = useDeleteFavoriteMutation();

  const isSaved = useMemo(() => {
    if (!property?.id || !favourites?.length) return false;
    return favourites.some((f) => String(f.id || f.property_id) === String(property.id));
  }, [favourites, property?.id]);

  const galleryImages = useMemo(() => {
    if (!property) return [];
    if (property.gallery?.length) {
      return property.gallery.map((g) => g.image_url || g).filter(Boolean);
    }
    return property.title_image ? [property.title_image] : [];
  }, [property]);

  const bedrooms = findParameter(property?.parameters, 'bedrooms');
  const bathrooms = findParameter(property?.parameters, 'bathrooms');
  const area =
    findParameter(property?.parameters, 'area') || findParameter(property?.parameters, 'sqft');

  const isTaken = isTakenListing(property);
  const isLuxury = isLuxuryListing(property);
  const statusText = isTaken
    ? 'Taken'
    : property?.status === '1' || property?.status === 1
      ? 'Available'
      : 'Unavailable';
  const statusBadgeClass = isTaken
    ? 'bg-rose-50 text-rose-700 border-rose-100'
    : property?.status === '1' || property?.status === 1
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : 'bg-amber-50 text-amber-800 border-amber-100';

  const typeLabel = getTransactionLabel(property?.property_type) || 'Listing';
  const typeBadge = getTransactionBadgeText(property?.property_type);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-slate-900 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Loading property…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-white border border-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Icon name="Home" size={28} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Property not found</h3>
            <p className="text-slate-500 mb-6">
              This listing may have been removed or the link is incorrect.
            </p>
            <Button onClick={() => navigate('/property-search-browse')}>Back to search</Button>
          </div>
        </div>
      </div>
    );
  }

  const propertySchema = listingSchema(property, slug_id);

  const nextImage = () => {
    if (galleryImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    }
  };
  const prevImage = () => {
    if (galleryImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    }
  };

  const handleSaveProperty = async () => {
    if (!isAuthenticated) {
      addToast('Please sign in to save properties.', 'info');
      navigate('/login', { state: { from: { pathname: `/property/${slug_id}` } } });
      return;
    }
    try {
      if (isSaved) {
        await deleteFavorite(property.id).unwrap();
        addToast('Removed from saved properties.', 'info');
      } else {
        await addFavorite(property.id).unwrap();
        addToast('Property saved to your shortlist.', 'success');
      }
    } catch {
      addToast('Could not update saved list.', 'error');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: property.title, url });
      } catch {
        /* cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        addToast('Link copied to clipboard.', 'info');
      } catch {
        addToast('Could not copy link.', 'error');
      }
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Inquiry about ${property.title}`);
    const body = encodeURIComponent(
      `${contactForm.message}\n\n—\n${contactForm.name}\n${contactForm.email}\n${contactForm.phone || ''}`
    );
    trackEvent('contact_agent_message', { property_id: property.id }, 'lead');
    if (property.email) {
      window.location.href = `mailto:${property.email}?subject=${subject}&body=${body}`;
    } else {
      if (!isAuthenticated) {
        navigate('/login', { state: { from: { pathname: `/property/${slug_id}` } } });
        return;
      }

      const wa = whatsappUrl(listingWhatsapp(property), {
        country: property.country,
        message: contactForm.message,
      });
      if (wa) {
        window.open(wa, '_blank', 'noopener,noreferrer');
      } else if (property.mobile) {
        window.open(`tel:${property.mobile}`);
      } else {
        addToast('No contact details available for this listing.', 'warning');
      }
    }
    setShowContactForm(false);
  };

  const openAgentWhatsApp = () => {
    if (!isAuthenticated) {
      addToast('Log in or sign up to contact the agent on WhatsApp.', 'info');
      return;
    }

    const wa = whatsappUrl(listingWhatsapp(property), {
      country: property.country,
      message:
        contactForm.message ||
        `Hello, I am interested in “${property.title || 'this listing'}” on HireBroker. Please share more details.`,
    });
    if (wa) {
      trackEvent('contact_agent_whatsapp', { property_id: property.id }, 'lead');
      window.open(wa, '_blank', 'noopener,noreferrer');
    }
  };

  const searchContext = {
    location: property?.city || property?.address || '',
    propertyType: property?.category?.category || '',
    priceRange: property?.price ? formatPrice(property.price, 'UGX') : '',
    queryString: '',
  };

  const locationLine = [property.address, property.city, property.state, property.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <SEO
        title={listingPageTitle(property)}
        description={listingPageDescription(property)}
        keywords={listingKeywords(property)}
        canonicalUrl={`https://hirebroker.co/property/${slug_id}`}
        ogImage={property?.title_image || galleryImages[0]}
        ogType="article"
        schema={propertySchema}
      />
      <Header />

      <main className="pt-16 pb-28 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumbs searchContext={searchContext} currentProperty={property} />
        </div>

        {/* Gallery */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="relative w-full h-[360px] sm:h-[440px] md:h-[540px] rounded-3xl overflow-hidden bg-slate-900 shadow-xl border border-slate-800/20">
            {galleryImages.length ? (
              <>
                <Image
                  src={galleryImages[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />

                {galleryImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prevImage}
                      className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 bg-white/15 hover:bg-white/25 text-white rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 transition-all"
                      aria-label="Previous image"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={nextImage}
                      className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 bg-white/15 hover:bg-white/25 text-white rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 transition-all"
                      aria-label="Next image"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </>
                )}

                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-semibold shadow"
                    style={TRANSACTION_BADGE_STYLE}
                  >
                    {typeBadge}
                  </span>
                  {property.category?.category && (
                    <span className="px-3 py-1.5 bg-slate-900/70 text-white rounded-full text-xs font-semibold backdrop-blur-sm">
                      {property.category.category}
                    </span>
                  )}
                  {isLuxury ? (
                    <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-400 text-slate-950 shadow">
                      Luxury
                    </span>
                  ) : null}
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-sm ${statusBadgeClass}`}
                  >
                    {statusText}
                  </span>
                </div>

                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-md flex items-center justify-center"
                    aria-label="Share"
                  >
                    <Icon name="Share2" size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveProperty}
                    disabled={addingFav || removingFav}
                    className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-md flex items-center justify-center"
                    aria-label="Save"
                  >
                    <Icon
                      name="Heart"
                      size={16}
                      className={isSaved ? 'text-rose-400 fill-rose-400' : 'text-white'}
                    />
                  </button>
                </div>

                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white text-lg sm:text-2xl font-bold drop-shadow-sm truncate">
                      {property.title}
                    </p>
                    <p className="text-white/85 text-sm flex items-center gap-1.5 mt-1 truncate">
                      <Icon name="MapPin" size={14} />
                      {locationLine}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-white text-xl sm:text-2xl font-bold drop-shadow-sm">
                      {formatPrice(property.price, 'UGX')}
                    </p>
                    <p className="text-white/80 text-xs mt-0.5">
                      {currentImageIndex + 1} / {galleryImages.length} photos
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Icon name="ImageOff" size={40} className="mx-auto mb-2" />
                  <p>No images available</p>
                </div>
              </div>
            )}
          </div>

          {galleryImages.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {galleryImages.map((src, index) => (
                <button
                  key={`${src}-${index}`}
                  type="button"
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-16 sm:w-24 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    index === currentImageIndex
                      ? 'border-slate-900 shadow-md'
                      : 'border-transparent opacity-80 hover:opacity-100'
                  }`}
                >
                  <Image src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-5">
              {/* Overview card */}
              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                      {property.title}
                    </h1>
                    <p className="text-slate-500 mt-2 flex items-start gap-1.5">
                      <Icon name="MapPin" size={16} className="mt-0.5 flex-shrink-0" />
                      {locationLine}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                      {formatPrice(property.price, 'UGX')}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {typeBadge}
                      <span className="text-slate-400"> · shown in {currency}</span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {bedrooms != null && bedrooms !== '' && (
                    <Stat icon="BedDouble" label="Bedrooms" value={bedrooms} />
                  )}
                  {bathrooms != null && bathrooms !== '' && (
                    <Stat icon="Bath" label="Bathrooms" value={bathrooms} />
                  )}
                  {area != null && area !== '' && (
                    <Stat icon="Maximize2" label="Area" value={area} />
                  )}
                  <Stat
                    icon="Eye"
                    label="Views"
                    value={property.total_view ?? property.total_views ?? '—'}
                  />
                </div>

                <div className="mt-5 flex flex-wrap gap-2 text-xs text-slate-500">
                  {property.post_created && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100">
                      <Icon name="Clock" size={12} />
                      Listed {property.post_created}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${statusBadgeClass}`}
                  >
                    {statusText}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100">
                    ID #{property.id}
                  </span>
                </div>
              </section>

              {property.description && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">About this property</h2>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {property.description}
                  </p>
                </section>
              )}

              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-5">Property details</h2>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
                  <DetailRow label="Category" value={property.category?.category} />
                  <DetailRow label="Type" value={typeBadge} />
                  <DetailRow label="Status" value={statusText} />
                  <DetailRow label="City" value={property.city} />
                  <DetailRow label="State / region" value={property.state} />
                  <DetailRow label="Country" value={property.country} />
                  <DetailRow label="Address" value={property.address} />
                  {property.parameters?.map((param) => (
                    <DetailRow key={param.id || param.name} label={param.name} value={param.value} />
                  ))}
                </div>
              </section>

              {property.assign_facilities?.length > 0 && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">Nearby facilities</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {property.assign_facilities.map((facility) => (
                      <div
                        key={facility.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100"
                      >
                        <div className="w-11 h-11 rounded-lg overflow-hidden bg-white flex-shrink-0">
                          {facility.image ? (
                            <Image
                              src={facility.image}
                              alt={facility.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon name="MapPin" size={16} className="text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {facility.name}
                          </p>
                          {facility.distance && (
                            <p className="text-xs text-slate-500">{facility.distance} away</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {property.latitude && property.longitude && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">Location map</h2>
                  <div className="w-full h-64 sm:h-80 rounded-xl overflow-hidden bg-slate-100 border border-slate-100">
                    <iframe
                      src={`https://maps.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map of ${property.title}`}
                    />
                  </div>
                </section>
              )}
            </div>

            {/* Sticky sidebar */}
<aside className="lg:col-span-1 space-y-4 lg:sticky lg:top-24 self-start font-sans">
  {/* Agent & Contact Card */}
  <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-xl shadow-slate-200/50 backdrop-blur-xl relative overflow-hidden">
    <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-100">
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
        Listing Agent
      </span>
      {property.agent_verified && (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Verified
        </span>
      )}
    </div>

    {/* Agent Info */}
    <div className="flex items-center gap-3.5 mb-6">
      <div className="relative">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-slate-900 to-slate-700 text-white flex items-center justify-center font-semibold text-lg shadow-md shadow-slate-900/10">
          {(property.customer_name || 'A').charAt(0).toUpperCase()}
        </div>
      </div>
      <div className="min-w-0">
        <h4 className="font-bold text-slate-900 text-base leading-snug truncate">
          {property.customer_name || 'Property Contact'}
        </h4>
        <p className="text-xs text-slate-500 font-medium">HireBroker Agent</p>
      </div>
    </div>

    {/* Contact Options */}
    <div className="space-y-3">
      {/* 1. Primary Action: Send Inquiry */}
      <button
        onClick={() => setShowContactForm(true)}
        className="w-full h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 shadow-md shadow-slate-900/10 active:scale-[0.99]"
      >
        <Icon name="MessageSquare" size={18} />
        <span>Send Inquiry</span>
      </button>

      {/* 2. Phone Call Button */}
      {(property.agent_phone_available || (isAuthenticated && property.mobile)) && (
        <button
          type="button"
          disabled={!isAuthenticated || !property.mobile}
          onClick={() => {
            if (!isAuthenticated) {
              addToast('Log in or sign up to view the agent’s phone number.', 'info');
              return;
            }
            if (property.mobile) window.location.href = `tel:${property.mobile}`;
          }}
          className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
        >
          <Icon name="Phone" size={17} className="text-slate-600" />
          <span>
            {isAuthenticated ? `Call ${property.mobile}` : 'Phone contact available · Log in to view'}
          </span>
        </button>
      )}

      {/* 3. WhatsApp Button */}
      {(property.agent_whatsapp_available || (isAuthenticated && listingWhatsapp(property))) && (
        <button
          type="button"
          disabled={!isAuthenticated || !listingWhatsapp(property)}
          onClick={openAgentWhatsApp}
          className="w-full h-11 px-4 rounded-xl border border-emerald-200 bg-emerald-50/40 text-emerald-800 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:border-slate-200 disabled:text-slate-500"
        >
          <Icon name="MessageCircle" size={18} className="text-emerald-600" />
          <span>
            {isAuthenticated
              ? 'WhatsApp Agent'
              : 'WhatsApp available · Log in to contact'}
          </span>
        </button>
      )}

      {/* 4. Optional Email Button */}
      {(property.email || property.agent_email_available) && (
        <button
          type="button"
          disabled={!isAuthenticated || !property.email}
          onClick={() => {
            if (property.email) {
              window.location.href = `mailto:${property.email}?subject=${encodeURIComponent(`Inquiry about ${property.title}`)}`;
            }
          }}
          className="w-full h-10 px-4 rounded-xl text-slate-600 hover:text-slate-900 text-xs font-medium flex items-center justify-center gap-2 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-500 disabled:hover:bg-transparent"
        >
          <Icon name="Mail" size={15} />
          <span>
            {isAuthenticated
              ? 'Email Agent Directly'
              : 'Email contact available · Log in to view'}
          </span>
        </button>
      )}
    </div>
  </div>

  {/* Quick Actions Card */}
  <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-xl shadow-slate-200/50 space-y-2.5">
    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 block mb-3">
      Quick Actions
    </span>

    <button
      onClick={handleSaveProperty}
      disabled={addingFav || removingFav}
      className={`w-full h-11 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 border ${
        isSaved
          ? 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'
          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
      }`}
    >
      <Icon name="Heart" size={17} className={isSaved ? 'fill-current text-rose-500' : 'text-slate-500'} />
      <span>{isSaved ? 'Saved to Favorites' : 'Save Listing'}</span>
    </button>

    <button
      onClick={handleShare}
      className="w-full h-11 px-4 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-slate-700 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:bg-slate-50"
    >
      <Icon name="Share2" size={17} className="text-slate-500" />
      <span>Share Listing</span>
    </button>

    <Link
      to="/notice-board"
      className="w-full h-11 px-4 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-100/80 text-slate-700 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
    >
      <Icon name="ClipboardList" size={17} className="text-slate-500" />
      <span>Request Specific Property</span>
    </Link>

    <Link
      to="/property-search-browse"
      className="w-full h-9 px-4 rounded-xl text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center justify-center gap-1.5 transition-colors"
    >
      <Icon name="ArrowLeft" size={14} />
      <span>Back to Search</span>
    </Link>
  </div>

  {/* Modern Glass Safety Tip */}
  <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 shadow-xl shadow-slate-900/10 border border-slate-800 relative overflow-hidden">
    <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-sky-500/10 rounded-full blur-xl pointer-events-none" />
    <div className="flex items-center gap-2 mb-2 text-sky-400 font-bold text-xs uppercase tracking-wider">
      <Icon name="ShieldCheck" size={16} />
      <span>Safety Tip</span>
    </div>
    <p className="text-xs text-slate-300 leading-relaxed font-normal">
      Always prioritize verified agents and avoid paying booking fees or deposits outside HireBroker channels.
    </p>
  </div>
</aside>



          </div>
        </div>

        {showContactForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200">
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <h3 className="text-lg font-semibold text-slate-900">Contact agent</h3>
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="p-2 rounded-lg hover:bg-slate-50 text-slate-500"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <form onSubmit={handleContactSubmit} className="p-5 space-y-4">
                <Field
                  label="Your name *"
                  value={contactForm.name}
                  onChange={(v) => setContactForm((p) => ({ ...p, name: v }))}
                  required
                />
                <Field
                  label="Email *"
                  type="email"
                  value={contactForm.email}
                  onChange={(v) => setContactForm((p) => ({ ...p, email: v }))}
                  required
                />
                <Field
                  label="Phone"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(v) => setContactForm((p) => ({ ...p, phone: v }))}
                  placeholder="+256 …"
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Message *
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm((p) => ({ ...p, message: e.target.value }))
                    }
                    rows={4}
                    required
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder={`I'm interested in ${property.title}…`}
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button type="submit" className="flex-1">
                    Send inquiry
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowContactForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

      </main>

      <Footer />
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-3 text-center">
    <Icon name={icon} size={16} className="mx-auto text-slate-500 mb-1" />
    <p className="text-sm font-bold text-slate-900">{value}</p>
    <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
  </div>
);

const DetailRow = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
};

export default PropertyDetail;
